import {Commits as CommitsList} from '@github-ui/commits/shared/Commits'
import {CommitsBlankState} from '@github-ui/commits/shared/CommitsBlankState'
import {useAnalytics} from '@github-ui/use-analytics'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useLoadDeferredCommitDataPaginated} from '@github-ui/commits/shared/useLoadDeferredCommitData'
import {Flash, SplitPageLayout} from '@primer/react'
import {useEffect} from 'react'
import {useCommitsLiveUpdates} from '../hooks/use-commits-live-updates'
import type {CommitsRouteWithHeaderDataPayload} from './route-payload-types'
import {pullRequestsCommitsRoute} from './commits-route'
import {pullRequestsLayoutRoute} from './layout-route'

const loggingPrefix = 'prx_commits.'

export function CommitsEntrypoint() {
  const {
    data: {pullRequest, aliveChannel, repository},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {
    data: {metadata, commitGroups, timeOutMessage, truncated},
    dataUpdatedAt,
  } = useRouteQuery(pullRequestsCommitsRoute, 'mainQuery')

  return (
    <CommitsComponent
      aliveChannel={aliveChannel}
      aliveChannelThrottleTimeout={10_000}
      commitGroups={commitGroups}
      dataUpdatedAt={dataUpdatedAt}
      metadata={metadata}
      pullRequest={pullRequest}
      repository={repository}
      timeOutMessage={timeOutMessage}
      truncated={truncated}
    />
  )
}

function CommitsComponent({
  commitGroups,
  metadata: {deferredCommitsDataUrl, commitHeadShaChannel},
  aliveChannel,
  pullRequest,
  repository,
  timeOutMessage,
  truncated,
  aliveChannelThrottleTimeout,
  dataUpdatedAt,
}: Pick<
  CommitsRouteWithHeaderDataPayload,
  'commitGroups' | 'pullRequest' | 'repository' | 'timeOutMessage' | 'truncated' | 'aliveChannel'
> & {
  metadata: Pick<CommitsRouteWithHeaderDataPayload['metadata'], 'deferredCommitsDataUrl' | 'commitHeadShaChannel'>
} & {
  aliveChannelThrottleTimeout?: number
  dataUpdatedAt?: number
}) {
  // Status updates
  useCommitsLiveUpdates(commitHeadShaChannel, aliveChannelThrottleTimeout)
  // Commit list updates
  useCommitsLiveUpdates(aliveChannel, aliveChannelThrottleTimeout)

  const deferredCommitsData = useLoadDeferredCommitDataPaginated(
    deferredCommitsDataUrl,
    //currently passing in 0 to start at the beginning, but if we want to paginate in the future this would be a variable
    0,
    pullRequest.commitsCount,
    dataUpdatedAt,
  )
  const {sendAnalyticsEvent} = useAnalytics()

  const loggingInfo = {commitCount: pullRequest.commitsCount, prNumber: pullRequest.number}

  useEffect(() => {
    sendAnalyticsEvent(`${loggingPrefix}page_view`, 'COMMITS_PAGE_VIEWED', loggingInfo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <h2 className="sr-only">Commits</h2>

      <SplitPageLayout.Content as="div" width="full" padding="none" className="mt-3">
        <div data-testid="commits-list" data-hpc>
          {commitGroups.length === 0 && <CommitsBlankState timeoutMessage={timeOutMessage} />}
          {commitGroups.length > 0 && (
            <>
              {truncated && (
                <Flash variant="warning" className="mb-3">
                  This pull request is big! We&apos;re only showing the most recent 250 commits
                </Flash>
              )}
              <CommitsList
                commitGroups={commitGroups}
                deferredCommitData={deferredCommitsData}
                repository={repository}
                loggingPrefix={loggingPrefix}
                loggingPayload={loggingInfo}
              />
            </>
          )}
        </div>
      </SplitPageLayout.Content>
    </>
  )
}
